/* eslint-disable @typescript-eslint/no-explicit-any */

"use client"

import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useState } from "react";
import { whenIdle } from "@/lib/whenIdle";

const PriceModal = dynamic(() => import("./PriceModal"), { ssr: false });

export type Trigger = "header" | "hero" | "lower_section" | "landing_page" | "other";

// The keyboard is up as the sheet opens (owner, 2026-11-03). The sheet focuses the search field as it opens
// (Sheet.tsx: [data-autofocus]), a moment after the tap; Android raises the keyboard for that, iPhone Safari only for a
// field focused in the tap itself. So on a touch screen an off-screen field takes the focus in the tap and the search
// field takes it over, the keyboard staying up.
// Only when the sheet opens in this tap (codeHere): with the modal's code still on its way, the keyboard comes up first and
// Chrome pans the page, leaving the title and the field above the screen. That visitor's Android still gets the keyboard
// from the sheet's focus; an iPhone waits for a tap on the field.
let codeHere = false;
function holdKeyboard() {
  if (!matchMedia("(pointer:coarse)").matches) return;
  const hold = document.createElement("input");
  hold.setAttribute("aria-hidden", "true");
  hold.tabIndex = -1;
  hold.style.cssText = "position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;font-size:16px;pointer-events:none";
  document.body.appendChild(hold);
  hold.focus({ preventScroll: true });
  // gone once the search field has the focus, or if the sheet never comes
  const done = () => {
    document.removeEventListener("focusin", done);
    clearTimeout(late);
    hold.remove();
  };
  document.addEventListener("focusin", done);
  const late = setTimeout(done, 4000);
}

// which element opened the window, for its sheet_opened event: the value an element names
// (data-panda-price-modal="hero"), else the nav's Book online, else a landing page's button
function triggerOf(el: HTMLElement): Trigger {
  const named = el.dataset.pandaPriceModal;
  if (named === "hero" || named === "lower_section") return named;
  if (el.closest("nav")) return "header";
  if (location.pathname.startsWith("/lp/")) return "landing_page";
  return "other";
}

/**
 * Provides a global, event-driven way to open the price modal without
 * converting SSG pages to client components. Use a data attribute like
 * `data-panda-price-modal` on any element or call `window.PandaHub.openPriceModal()`.
 */
export default function PriceModalProvider() {
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState<Trigger>("other");
  // the modal (and its code) only once the page has loaded and is idle, or at once if it's opened before that
  const [ready, setReady] = useState(false);
  useEffect(
    () =>
      whenIdle(() => {
        setReady(true);
        void import("./PriceModal").then(() => {
          codeHere = true;
        });
      }),
    [],
  );

  const openModal = useCallback((by: Trigger = "other") => {
    setTrigger(by);
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => setOpen(false), []);

  // Event delegation: listen for clicks on elements with the data attribute
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const el = target.closest<HTMLElement>('[data-panda-price-modal]');
      if (el) {
        e.preventDefault();
        if (codeHere) holdKeyboard();
        openModal(triggerOf(el));
      }
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [openModal]);

  // Tiny global helper for scripts and debugging
  useEffect(() => {
    const api = {
      openPriceModal: () => openModal(),
      closePriceModal: closeModal,
    } as const;
    (window as any).PandaHub = Object.assign((window as any).PandaHub || {}, api);
    return () => {
      const wh = (window as any).PandaHub || {};
      if (wh.openPriceModal === api.openPriceModal) delete wh.openPriceModal;
      if (wh.closePriceModal === closeModal) delete wh.closePriceModal;
    };
  }, [openModal, closeModal]);

  const props = useMemo(() => ({ open, onOpenChange: setOpen, trigger }), [open, trigger]);

  return ready || open ? <PriceModal {...props} /> : null;
}


